import { format, formatDistance } from "date-fns";

/**
 * Coerce an API numeric value to a `number`. Prisma `Decimal` fields are
 * serialised as strings over the wire, so most money/quantity values arrive as
 * strings and must be parsed before any arithmetic or formatting.
 */
export const toNumber = (value: unknown): number => {
  if (value === null || value === undefined || value === "") return 0;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
};

// Currency formatting (accepts the string Decimals the API returns)
export const formatCurrency = (
  amount: number | string | null | undefined,
  currency: string = "PKR",
): string => {
  return new Intl.NumberFormat("en-PK", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(toNumber(amount));
};

// Number formatting
export const formatNumber = (
  num: number | string | null | undefined,
  decimals: number = 2,
): string => {
  return toNumber(num).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
};

/** Humanise an UPPER_SNAKE enum value, e.g. `BANK_TRANSFER` → `Bank Transfer`. */
export const humanizeEnum = (value?: string | null): string => {
  if (!value) return "—";
  return value
    .toLowerCase()
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

// Date formatting
export const formatDate = (date: Date | string): string => {
  const d = typeof date === "string" ? new Date(date) : date;
  return format(d, "dd MMM yyyy");
};

export const formatDateTime = (date: Date | string): string => {
  const d = typeof date === "string" ? new Date(date) : date;
  return format(d, "dd MMM yyyy HH:mm");
};

export const getRelativeTime = (date: Date | string): string => {
  const d = typeof date === "string" ? new Date(date) : date;
  return formatDistance(d, new Date(), { addSuffix: true });
};

// Calculations
export const calculateYield = (input: number, output: number): number => {
  if (input === 0) return 0;
  return (output / input) * 100;
};

export const calculateWastage = (input: number, output: number): number => {
  return input - output;
};

export const calculateWastagePercentage = (
  input: number,
  waste: number,
): number => {
  if (input === 0) return 0;
  return (waste / input) * 100;
};

// Years of service calculation
export const calculateYearsOfService = (joiningDate: Date | string): number => {
  const joining =
    typeof joiningDate === "string" ? new Date(joiningDate) : joiningDate;
  const now = new Date();
  let years = now.getFullYear() - joining.getFullYear();
  const monthDiff = now.getMonth() - joining.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < joining.getDate())) {
    years--;
  }

  return Math.max(0, years);
};

/**
 * Tailwind badge classes keyed by backend enum values (case-insensitive).
 * Covers OrderStatus, ProductionStatus, PaymentStatus, SalaryStatus and methods.
 */
export const getStatusColor = (status?: string | null): string => {
  if (!status) return "bg-slate-700 text-slate-300";
  const colors: Record<string, string> = {
    // Order / production lifecycle
    PENDING: "bg-amber-500/15 text-amber-400 ring-1 ring-amber-500/30",
    DRAFT: "bg-slate-500/15 text-slate-300 ring-1 ring-slate-500/30",
    CONFIRMED: "bg-blue-500/15 text-blue-400 ring-1 ring-blue-500/30",
    IN_PROGRESS: "bg-blue-500/15 text-blue-400 ring-1 ring-blue-500/30",
    PACKED: "bg-violet-500/15 text-violet-400 ring-1 ring-violet-500/30",
    DISPATCHED: "bg-cyan-500/15 text-cyan-400 ring-1 ring-cyan-500/30",
    DELIVERED: "bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30",
    COMPLETED: "bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30",
    CANCELLED: "bg-red-500/15 text-red-400 ring-1 ring-red-500/30",
    REFUNDED: "bg-orange-500/15 text-orange-400 ring-1 ring-orange-500/30",
    FAILED: "bg-red-500/15 text-red-400 ring-1 ring-red-500/30",
    // Salary
    PAID: "bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30",
    PARTIAL: "bg-amber-500/15 text-amber-400 ring-1 ring-amber-500/30",
    UNPAID: "bg-red-500/15 text-red-400 ring-1 ring-red-500/30",
  };
  return colors[status.toUpperCase()] || "bg-slate-700 text-slate-300";
};

// Generate receipt/invoice number
export const generateInvoiceNumber = (): string => {
  const date = new Date();
  const random = Math.floor(Math.random() * 10000);
  return `INV-${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, "0")}${String(date.getDate()).padStart(2, "0")}-${String(random).padStart(4, "0")}`;
};

// Export to CSV
export const exportToCSV = (data: any[], filename: string): void => {
  if (!data || data.length === 0) {
    alert("No data to export");
    return;
  }

  const headers = Object.keys(data[0]);
  const csv = [
    headers.join(","),
    ...data.map((row) =>
      headers
        .map((header) => {
          const value = row[header];
          return typeof value === "string" && value.includes(",")
            ? `"${value}"`
            : value;
        })
        .join(","),
    ),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

// Validation
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const isValidPhone = (phone: string): boolean => {
  const phoneRegex =
    /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/;
  return phoneRegex.test(phone);
};

// Capitalize first letter
export const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// Get initials from name
export const getInitials = (name: string): string => {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();
};

// Calculate percentage
export const calculatePercentage = (value: number, total: number): number => {
  if (total === 0) return 0;
  return (value / total) * 100;
};

// Get color for percentage
export const getPercentageColor = (percentage: number): string => {
  if (percentage >= 75) return "text-green-500";
  if (percentage >= 50) return "text-yellow-500";
  return "text-red-500";
};

// Truncate text
export const truncateText = (text: string, length: number): string => {
  return text.length > length ? `${text.substring(0, length)}...` : text;
};
